import { useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

export const useProducts = () => {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);

  useEffect(() => {
    fetchProducts();
    fetchSuppliers();
  }, []);

  // Función interna para obtener el rol REAL desde la base de datos
  // Evita problemas de caché con los metadatos del JWT
  const getRealRole = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 'viewer';
    
    const { data } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();
      
    return data?.role || 'viewer';
  };

  // --- OPERACIONES DE PRODUCTOS ---
  const fetchProducts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('products_1772625185765')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setProducts(data);
    }
    setLoading(false);
  };

  const addProduct = async (productData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const newProduct = {
      name: productData.name,
      price: parseFloat(productData.price),
      stock: parseInt(productData.stock, 10),
      category: productData.category,
      supplier_id: productData.supplier_id || null,
      user_id: user.id
    };

    const { data, error } = await supabase
      .from('products_1772625185765')
      .insert([newProduct])
      .select();

    if (!error && data) {
      setProducts((prev) => [data[0], ...prev]);
    }
  };

  const updateProduct = async (id, updatedData) => {
    const role = await getRealRole();
    if (role !== 'admin' && role !== 'editor') {
      alert('Permiso denegado: Solo administradores y editores pueden modificar productos.');
      return;
    }

    const updatePayload = {
      name: updatedData.name,
      price: parseFloat(updatedData.price),
      stock: parseInt(updatedData.stock, 10),
      category: updatedData.category,
      supplier_id: updatedData.supplier_id || null,
    };

    const { error } = await supabase
      .from('products_1772625185765')
      .update(updatePayload)
      .eq('id', id);

    if (!error) {
      setProducts((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ...updatePayload } : p))
      );
    }
  };

  const deleteProduct = async (id) => {
    const role = await getRealRole();
    if (role !== 'admin') {
      alert('Permiso denegado: Solo los administradores pueden eliminar productos.');
      return;
    }

    const { error } = await supabase
      .from('products_1772625185765')
      .delete()
      .eq('id', id);

    if (!error) {
      setProducts((prev) => prev.filter((p) => p.id !== id));
    }
  };

  // --- OPERACIONES DE PROVEEDORES ---
  const fetchSuppliers = async () => {
    setLoadingSuppliers(true);
    const { data, error } = await supabase
      .from('suppliers_1773060688216')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setSuppliers(data);
    }
    setLoadingSuppliers(false);
  };

  const addSupplier = async (supplierData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const newSupplier = {
      name: supplierData.name,
      contact: supplierData.contact || '',
      phone: supplierData.phone || '',
      user_id: user.id
    };

    const { data, error } = await supabase
      .from('suppliers_1773060688216')
      .insert([newSupplier])
      .select();

    if (!error && data) {
      setSuppliers((prev) => [data[0], ...prev]);
    }
  };

  const updateSupplier = async (id, updatedData) => {
    const role = await getRealRole();
    if (role !== 'admin' && role !== 'editor') {
      alert('Permiso denegado: Solo administradores y editores pueden modificar proveedores.');
      return;
    }

    const updatePayload = {
      name: updatedData.name,
      contact: updatedData.contact || '',
      phone: updatedData.phone || '',
    };

    const { error } = await supabase
      .from('suppliers_1773060688216')
      .update(updatePayload)
      .eq('id', id);

    if (!error) {
      setSuppliers((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...updatePayload } : s))
      );
    }
  };

  const deleteSupplier = async (id) => {
    const role = await getRealRole();
    if (role !== 'admin') {
      alert('Permiso denegado: Solo los administradores pueden eliminar proveedores.');
      return;
    }

    const { error } = await supabase
      .from('suppliers_1773060688216')
      .delete()
      .eq('id', id);

    if (!error) {
      setSuppliers((prev) => prev.filter((s) => s.id !== id));
    }
  };

  return {
    products,
    loading,
    addProduct,
    updateProduct,
    deleteProduct,
    suppliers,
    loadingSuppliers,
    addSupplier,
    updateSupplier,
    deleteSupplier,
  };
};